import React, { useState, useEffect, useRef } from "react";
import { Plane, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function AirportSearchInput({ label, value, onChange, testId }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (query.trim().length > 0) {
        api.get(`/airports?q=${encodeURIComponent(query)}`).then((res) => setResults(res.data.results)).catch(() => {});
      } else {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(handler);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="text-sm font-medium text-[#1A1A1A] mb-1.5 block">{label}</label>
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-secondary)]" />
        <Input
          data-testid={testId}
          className="pl-9 focus-visible:ring-[var(--brand-primary)]"
          placeholder="Search city or airport code"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            onChange("");
          }}
          onFocus={() => setOpen(true)}
        />
      </div>
      {open && query.trim().length > 0 && (
        <div data-testid={`${testId}-dropdown`} className="absolute z-20 mt-1 w-full bg-white border border-[#E5E1DA] rounded-md shadow-lg max-h-56 overflow-y-auto">
          {results.map((r) => (
            <button
              key={r.code}
              type="button"
              data-testid={`${testId}-option-${r.code}`}
              onClick={() => {
                const display = `${r.city} (${r.code}) - ${r.name}`;
                setQuery(display);
                onChange(display);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)] flex items-center gap-2 text-sm border-b border-[#E5E1DA] last:border-0"
            >
              <Plane size={14} className="text-[var(--brand-primary)] shrink-0" />
              <span>{r.city} <span className="text-[var(--brand-primary)] font-medium">({r.code})</span> - {r.name}</span>
            </button>
          ))}
          <button
            type="button"
            data-testid={`${testId}-manual-entry`}
            onClick={() => {
              onChange(query.trim());
              setOpen(false);
            }}
            className="w-full text-left px-4 py-2.5 hover:bg-[color-mix(in_oklab,var(--brand-primary),transparent_90%)] flex items-center gap-2 text-sm text-[#54595f] italic"
          >
            <Search size={14} className="shrink-0" />
            <span>Can't find your airport? Use "<strong className="not-italic text-[var(--brand-primary)]">{query.trim()}</strong>" as typed</span>
          </button>
        </div>
      )}
    </div>
  );
}
